import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { useAddRoomAttachmentMutation } from '../../../../api/addRoomAttachment';
import { useSetUnilateralMutation } from '../../../../api/setUnilateral';
import { modalNames } from '../../../../shared/configs/modal';
import { useModalMethods } from '../../../../shared/packages/modalManager';
import { fieldNames } from '../../constants';
import { roomAttachmentTypeIds } from '../../../../shared/configs/attachments';
import { useSearchParamsMethods } from '../../../../shared/packages/searchParamsManager';
import { searchParamKeys } from '../../../../shared/configs/searchParams';
import { parseAcceptanceSlug } from '../../../../shared/packages/acceptance';
import { message } from 'antd';
import { useAcceptanceViewFlatsInvalidate } from '../../../../api/acceptanceViewFlats';
import { useInvalidateRoomInfo } from '../../../../api/roomInfo';
import { useInvalidateRoomAttachments } from '../../../../api/roomAttachments';

const modalName = modalNames.attachmentsForm;

export const SubmitContext = createContext();
const SubmitStateContext = createContext();
const SubmitSetStateContext = createContext();

export const useSubmitState = () => useContext(SubmitStateContext);
export const useSubmitSetState = () => useContext(SubmitSetStateContext);

export const SubmitProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [submitState, setSubmitState] = useState(null);

  const submitStateRef = useRef(submitState);
  submitStateRef.current = submitState;

  const { getParams } = useSearchParamsMethods();

  const { mutateAsync: addAttachment } = useAddRoomAttachmentMutation();
  const { mutateAsync: setUnilateral } = useSetUnilateralMutation();

  const invalidateFlatsView = useAcceptanceViewFlatsInvalidate();
  const invalidateRoomInfo = useInvalidateRoomInfo();
  const invalidateAttachments = useInvalidateRoomAttachments();

  const { getProps, closeAndKeepProps } = useModalMethods();

  const onSubmit = useCallback(
    async (values) => {
      const { room, record } = getProps(modalName) ?? {};

      const addressId = room?.id;

      const acceptanceClientId = record?.id;

      const { acceptanceId } = parseAcceptanceSlug(
        getParams(searchParamKeys.acceptance)
      );

      setLoading(true);

      const {
        [fieldNames.files]: files,
        [fieldNames.clientId]: clientId,
        ...rest
      } = values;

      if (
        !submitStateRef.current ||
        submitStateRef.current.source === 'attachment'
      ) {
        const addAttachmentResult = await addAttachment({
          addressId,
          typeId: roomAttachmentTypeIds.unilateral,
          acceptanceClientId,
          data: rest,
          file: files[0],
          showErrorMessage: false,
        });

        if (addAttachmentResult === 'Error') {
          setSubmitState({
            status: 'error',
            source: 'attachment',
            message: (
              <>
                Не удалось добавить вложение. Из-за этого невозможно уставновить
                одностороннюю передачу. <br /> Попробуйте отправить еще раз.
                <br /> Если это не первая попытка, обратитесь в техподддержку.
              </>
            ),
          });

          setLoading(false);
          return;
        }

        // На всякий случай сбрасываем состояние
        setSubmitState(null);

        const setUnilateralResult = await setUnilateral({
          addressId: room?.id,
          acceptanceId,
          clientId,
        });

        if (setUnilateralResult === 'Error') {
          setSubmitState({
            status: 'error',
            source: 'unilateral',
            message: (
              <>
                Не удалось установить одностороннюю передачу. <br /> Попробуйте
                отправить еще раз. <br /> Если это не первая попытка, обратитесь
                в техподддержку.
              </>
            ),
          });

          setLoading(false);
          return;
        }

        if (setUnilateralResult?.success === false) {
          message.error(
            setUnilateralResult.message ??
              'Не удалось установить одностороннюю передачу'
          );
        }

        setLoading(false);
        invalidateFlatsView();
        invalidateRoomInfo({ addressId });
        invalidateAttachments({ addressId });
        closeAndKeepProps(modalName);
        return;
      }

      if (submitStateRef.current.source === 'unilateral') {
        const setUnilateralResult = await setUnilateral({
          addressId: room?.id,
          acceptanceId,
          clientId,
        });

        if (setUnilateralResult === 'Error') {
          setSubmitState({
            status: 'error',
            source: 'unilateral',
            message: (
              <>
                Не удалось установить одностороннюю передачу. <br /> Попробуйте
                отправить еще раз. <br /> Если это не первая попытка, обратитесь
                в техподддержку.
              </>
            ),
          });

          setLoading(false);
          return;
        }

        if (setUnilateralResult?.success === false) {
          message.error(
            setUnilateralResult.message ??
              'Не удалось установить одностороннюю передачу'
          );
        }

        setLoading(false);
        invalidateFlatsView();
        invalidateRoomInfo({ addressId });
        closeAndKeepProps(modalName);
        return;
      }

      setLoading(false);
    },
    [
      addAttachment,
      closeAndKeepProps,
      getParams,
      getProps,
      invalidateFlatsView,
      invalidateRoomInfo,
      setUnilateral,
      invalidateAttachments,
    ]
  );

  return (
    <SubmitContext.Provider value={{ onSubmit, loading }}>
      <SubmitStateContext.Provider value={submitState}>
        <SubmitSetStateContext.Provider value={setSubmitState}>
          {children}
        </SubmitSetStateContext.Provider>
      </SubmitStateContext.Provider>
    </SubmitContext.Provider>
  );
};
